import React, {useEffect, useState} from "react";
import {Button, Col, Container, Row} from "reactstrap";
import Loading from "../components/Loading";
import {useAuth0, withAuthenticationRequired} from "@auth0/auth0-react";
import InfiniteScroll from "react-infinite-scroll-component";
import SearchResult from "../components/SearchResult";
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, FormControlLabel,
    InputAdornment,
    Snackbar, Switch,
    TextField
} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import {firebaseService} from "../services/firebaseService";
import {analytics} from "../firebase";
import { logEvent } from "firebase/analytics";
import QuickBookModal from "../components/QuickBookModal";

export const SearchComponent = () => {
    const [usersLibrary, setUsersLibrary] = useState([]);
    const [usersLibraryLoaded, setUsersLibraryLoaded] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchQueryTitle, setSearchQueryTitle] = useState("");
    const [searchQueryAuthor, setSearchQueryAuthor] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingUpdate, setLoadingUpdate] = useState(false);
    const [noResults, setNoResults] = useState(false);
    const [searchResults, setSearchResults] = useState({
        numFound: 0,
        docs: []
    });
    const [paginatedResults, setPaginatedResults] = useState({
        docs: []
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [bookToRemove, setBookToRemove] = useState(null);
    const [searchMode, setSearchMode] = useState("basic"); // "basic" or "advanced"
    const [ownBookTitle, setOwnBookTitle] = useState("");
    const [ownBookAuthor, setOwnBookAuthor] = useState("");
    const [ownBookMarkAsRead, setOwnBookMarkAsRead] = useState(false);
    const [showAddBookManuallyDialog, setShowAddBookManuallyDialog] = useState(false);

    const [showBookModal, setShowBookModal] = useState(false);
    const [bookModalData, setBookModalData] = useState(null);
    const [bookModalNavValue, setBookModalNavValue] = useState("details");

    const pageSize = 10;

    const {user} = useAuth0();

    const openConfirmDialog = (book, event) => {
        setConfirmDialogOpen(true);
        setBookToRemove(book);
    }

    const openBookModal = (book, event) => {
        const libraryItem = usersLibrary.find((item) => item.book.key === book.key);
        if (libraryItem) {
            setBookModalData(libraryItem);
            setShowBookModal(true);
        }
    }

    const closeBookModal = () => {
        setShowBookModal(false);
        setBookModalData(null);
    }

    const removeFromLibrary = async (book) => {
        logEvent(analytics, 'book_removed');
        setLoadingUpdate(true);
        const firebaseItem = usersLibrary.find((item) => item.book.key === book.key);

        await firebaseService.deleteBook(firebaseItem.id);

        setBookToRemove(null);
        setConfirmDialogOpen(false)

        getLibrary().then((data) => {
            setUsersLibrary(data);
            setLoadingUpdate(false);
            setSnackbarMessage("Book removed from library");
            setShowSnackbar(true);
        });
    }

    const getCoverData = async (book) => {
        let result = "";

        if (book.cover_i) {
            let blob = await fetch(`https://covers.openlibrary.org/b/ID/${book.cover_i}-M.jpg`).then((response) => {
                return response.blob();
            }).catch((error) => {
                console.log("Error fetching cover image: ", error);
                return "";
            });

            if (blob) {
                result = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            }

        }

        return result;
    }

    const addToLibrary = async (book) => {
        logEvent(analytics, 'book_added');
        setLoadingUpdate(true);
        await getCoverData(book).then(async (coverBase64) => {

            await firebaseService.addBook(book, user, coverBase64, ownBookMarkAsRead)

            getLibrary().then((data) => {
                setUsersLibrary(data);
                setLoadingUpdate(false);
                setSnackbarMessage("Added to library");
                setShowSnackbar(true);
            });
        });
    }

    const getLibrary = async () => {
        setUsersLibraryLoaded(true);
        return await firebaseService.getUserLibrary(user)
    }

    const isInLibrary = (book) => {
        return usersLibrary.find((item) => item.book.key === book.key);
    }

    const isReading = (book) => {
       if (isInLibrary(book)) {
              return usersLibrary.find((item) => item.book.key === book.key).currentlyReading;
       }
         return false;
    }

    const hasCompleted = (book) => {
        if (isInLibrary(book)) {
            return usersLibrary.find((item) => item.book.key === book.key).completed;
        }
        return false;
    }

    const toggleSearchMode = () => {
        if (searchMode === "basic") {
            setSearchMode("advanced");
        } else {
            setSearchMode("basic");
        }
        setSearchQueryAuthor("");
        setSearchQueryTitle("");
        setSearchQuery("");
    }

    const clearSearchResults = () => {
        setSearchQuery("");
        setSearchQueryTitle("");
        setSearchQueryAuthor("");
        setSearchResults({
            numFound: 0,
            docs: []
        });
        setPaginatedResults({
            docs: []
        });
        setCurrentPage(1);
    }

    useEffect(() => {
        if (user && !usersLibraryLoaded) {
            getLibrary().then((data) => {
                setUsersLibrary(data);
            });
        }
    })

    const search = async () => {
        logEvent(analytics, 'search_made');
        setLoading(true);
        setSearchResults({
            numFound: 0,
            docs: []
        });
        setPaginatedResults({
            docs: []
        });
        setCurrentPage(1);
        setHasMore(true);
        setNoResults(false);

        let query = "q=" + searchQuery.replace(" ", "+");

        if (searchMode === "advanced") {
            const title = (searchQueryTitle.length > 0) ? `title=${searchQueryTitle.replace(" ", "+")}` : "";
            const author = (searchQueryAuthor.length > 0) ? `author=${searchQueryAuthor.replace(" ", "+")}` : "";
            query = `${title}${(title && author) ? "&" : ""}${author}`;
        }

        const limit = "limit=100";
        const fields = "fields=key,title,author_name,cover_i,subject,first_publish_year";
        const url = `https://openlibrary.org/search.json?${query}&${limit}&${fields}`;
        // const headers = new Headers({
        //     "User-Agent": "Lubi/0.1 (benspickett@gmail.com)"
        // });
        const options = {
            method: 'GET',
            // headers: headers
        };

        const response = await fetch(url, options);
        const results = await response.json();

        setSearchResults(results);
        const firstPageResults = {
            docs: results.docs.slice(0, pageSize)
        }
        setPaginatedResults(firstPageResults);
        setNoResults(results.docs.length === 0);
        setLoading(false);
    }

    const getNextPage = async () => {

        if (paginatedResults.docs.length === searchResults.docs.length) {
            setHasMore(false);
            return;
        }

        const nextPage = currentPage + 1;
        const start = (nextPage - 1) * pageSize;
        const end = nextPage * pageSize;
        const nextResults = {
            docs: searchResults.docs.slice(start, end)
        }

        setTimeout(() => {
            setPaginatedResults({
                docs: paginatedResults.docs.concat(nextResults.docs)
            });

            setCurrentPage(nextPage);
        }, 2000);

    }

    const foundResultsText = (numFound) => {
        const paginatedResultsLength = paginatedResults.docs.length;
        if (numFound === 1) {
            return "1 result found";
        }

        return `Showing ${paginatedResultsLength} of ${numFound} results`;
    }

    // === LIBRARY === //
    const markAsReading = async (book, event) => {
        logEvent(analytics, 'book_updated_to_reading');
        setLoadingUpdate(true);
        const firebaseItem = usersLibrary.find((item) => item.book.key === book.key);

        await firebaseService.updateBook(firebaseItem.id, {
            completed: false,
            currentlyReading: true,
            dateCompleted: null
        });
        setUsersLibrary(usersLibrary.map((item) => item.id === book.id ? {...item, completed: false} : item));
        setBookModalData({...bookModalData, completed: false, currentlyReading: true, dateCompleted: null});

        await firebaseService.addActivity(user, firebaseItem, "started reading");

        getLibrary().then((data) => {
            setUsersLibrary(data);
            setLoadingUpdate(false);
            setSnackbarMessage("Marked as reading");
            setShowSnackbar(true);
        });
    }

    const markAsNotReading = async (book, event) => {
        logEvent(analytics, 'book_updated_to_not_reading');
        setLoadingUpdate(true);
        const firebaseItem = usersLibrary.find((item) => item.book.key === book.key);
        await firebaseService.updateBook(firebaseItem.id, {
            completed: false,
            currentlyReading: false,
            dateCompleted: null
        })
        setUsersLibrary(usersLibrary.map((item) => item.id === book.id ? {...item, completed: false} : item));
        setBookModalData({...bookModalData, currentlyReading: false, dateCompleted: null, completed: false});

        getLibrary().then((data) => {
            setUsersLibrary(data);
            setLoadingUpdate(false);
            setSnackbarMessage("Marked as not reading");
            setShowSnackbar(true);
        });
    }

    const markCompleted = async (book, event) => {
        logEvent(analytics, 'book_updated_to_completed');
        setLoadingUpdate(true);
        const firebaseItem = usersLibrary.find((item) => item.book.key === book.key);
        await firebaseService.updateBook(firebaseItem.id, {
            completed: true,
            currentlyReading: false,
            dateCompleted: new Date()
        })
        setUsersLibrary(usersLibrary.map((item) => item.id === book.id ? {...item, completed: true} : item));
        setBookModalData({...bookModalData, completed: true, currentlyReading: false, dateCompleted: new Date()});

        await firebaseService.addActivity(user, firebaseItem, "completed");

        getLibrary().then((data) => {
            setUsersLibrary(data);
            setLoadingUpdate(false);
            setSnackbarMessage("Marked as completed");
            setShowSnackbar(true);
        });
    };

    return (
        <>
            <NavBar />
            <Container className="flex-grow-1 mt-5">
                <Container className="mb-5 search-container">
                    <div className="search-top-block">
                        <h4>Search</h4>

                        <div className="advanced-search-link">
                            <div className="link-text" onClick={()=>{toggleSearchMode()}}>
                                <FontAwesomeIcon icon="sliders-h"/>
                                <span>{(searchMode === "basic") ? "Advanced search" : "Basic search"}</span>
                            </div>
                        </div>

                        {/*Search Section*/}
                        <Row className="align-items-center mb-5 mt-4 text-center text-md-left">
                            <Col md>
                                <div className={`${searchMode} search-input-container`}>
                                    {(searchMode === "basic") &&
                                        <TextField label="Powered by openlibrary.org" variant="outlined" className="search-text-field"
                                                   slotProps={{
                                                       input: {
                                                           startAdornment: (
                                                               <InputAdornment position="start">
                                                                   <FontAwesomeIcon icon="search"/>
                                                               </InputAdornment>
                                                           ),
                                                       },
                                                   }}
                                                   onChange={(e) => {
                                                       setSearchQuery(e.target.value);
                                                   }}
                                                   onKeyDown={(e) => {
                                                       if (e.key === 'Enter') {search();}}}
                                        />
                                    }

                                    {(searchMode === "advanced") &&
                                        <>
                                            <TextField label="Title" variant="outlined" className="search-text-field"
                                                       slotProps={{
                                                           input: {
                                                               startAdornment: (
                                                                   <InputAdornment position="start">
                                                                       <FontAwesomeIcon icon="book-open"/>
                                                                   </InputAdornment>
                                                               ),
                                                           },
                                                       }}
                                                       onChange={(e) => {
                                                           setSearchQueryTitle(e.target.value);
                                                       }}
                                            />
                                            <TextField label="Author" variant="outlined" className="search-text-field"
                                                       slotProps={{
                                                           input: {
                                                               startAdornment: (
                                                                   <InputAdornment position="start">
                                                                       <FontAwesomeIcon icon="user"/>
                                                                   </InputAdornment>
                                                               ),
                                                           },
                                                       }}
                                                       onChange={(e) => {
                                                           setSearchQueryAuthor(e.target.value);
                                                       }}
                                            />
                                        </>
                                    }

                                    {(searchMode === "basic") &&
                                        <button className="btn btn-primary" disabled={loading || searchQuery.length === 0}
                                                onClick={() => search()}>
                                            Search
                                        </button>
                                    }

                                    {(searchMode === "advanced") &&
                                        <button className="btn btn-primary" disabled={loading || (searchQueryAuthor.length === 0 && searchQueryTitle.length === 0)}
                                                onClick={() => search()}>
                                            Search
                                        </button>
                                    }
                                </div>
                            </Col>
                        </Row>

                        <div className="clear-search-results-link">
                            {searchResults.numFound > 0 &&
                                <div className="link-text" onClick={()=>{clearSearchResults()}}>
                                    <span>Clear search results</span>
                                </div>
                            }
                        </div>

                    </div>


                    {/*Results Section*/}
                    <Row className="align-items-center mb-5 text-center text-md-left">
                        <Col md>
                            {loading && <Loading/>}

                            {noResults && !loading && (
                                <div>

                                    <div style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
                                        <h4>No results found</h4>

                                        <Button onClick={() => {
                                            setShowAddBookManuallyDialog(true);
                                        }}>
                                            Add your own book
                                        </Button>

                                    </div>
                                </div>
                            )}

                            {searchResults.numFound > 0 && (
                                <>
                                    <h4>{foundResultsText(searchResults.numFound)}</h4>
                                    <br/>
                                    <div className="result-block-container">

                                        <InfiniteScroll
                                            dataLength={paginatedResults.docs.length}
                                            next={getNextPage}
                                            hasMore={hasMore}
                                            loader={<Loading/>}
                                            style={{paddingTop: "10px", display: "flex", flexDirection: "row", flexWrap: "wrap", justifyContent: "space-around", alignItems: "stretch"}}
                                        >
                                            {paginatedResults.docs.map((book, index) => (
                                                <SearchResult
                                                    key={book.id}
                                                    book={book}
                                                    addToLibrary={addToLibrary}
                                                    openConfirmDialog={openConfirmDialog}
                                                    isInLibrary={isInLibrary}
                                                    isReading={isReading}
                                                    hasCompleted={hasCompleted}
                                                    openBookModal={openBookModal}
                                                />
                                            ))}
                                        </InfiniteScroll>
                                    </div>
                                </>
                            )}
                        </Col>
                    </Row>

                    {loadingUpdate &&
                        <div className="loading-update-container">
                            <Loading />
                        </div>
                    }

                    <Snackbar
                        open={showSnackbar}
                        onClose={() => setShowSnackbar(false)}
                        autoHideDuration={3000}
                        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                    >
                        <Alert
                            onClose={() => setShowSnackbar(false)}
                            severity="success"
                            variant="filled"
                            sx={{ width: '100%' }}
                        >
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>

                </Container>
            </Container>

            <QuickBookModal
                showBookModal={showBookModal}
                closeBookModal={closeBookModal}
                book={bookModalData}
                loadingUpdate={loadingUpdate}
                bookModalNavValue={bookModalNavValue}
                setBookModalNavValue={setBookModalNavValue}
                markAsReading={markAsReading}
                markAsNotReading={markAsNotReading}
                markCompleted={markCompleted}
                openConfirmDialog={openConfirmDialog}
            />

            <Dialog fullWidth className="confirm-remove-dialog" open={confirmDialogOpen} onClose={() => {
                setConfirmDialogOpen(false)
            }}>
                {bookToRemove &&
                    <>
                        <DialogTitle>Warning</DialogTitle>
                        <DialogContent>Are you sure you want to remove {bookToRemove.title}?</DialogContent>
                        <DialogActions className="dialog-actions">
                            <Button className="dialog-button" color="warning"  onClick={() => {
                                removeFromLibrary(bookToRemove)
                            }}>Yes</Button>
                            <Button className="dialog-button" color="primary" onClick={() => {
                                setConfirmDialogOpen(false)
                            }}>No</Button>
                        </DialogActions>
                    </>
                }
            </Dialog>

            <Dialog fullWidth className="add-book-manually-dialog" open={showAddBookManuallyDialog}
                    onClose={() => {
                setShowAddBookManuallyDialog(false)
            }}>
                <DialogTitle>Add book manually</DialogTitle>
                <DialogContent style={{
                    display: "flex",
                    flexDirection: "column",
                }}>
                    <TextField label="Title" variant="outlined" className="search-text-field"
                               style={{
                                   margin: "10px 0"
                               }}
                               onChange={(e) => {
                                   setOwnBookTitle(e.target.value);
                               }}
                    />
                    <TextField label="Author" variant="outlined" className="search-text-field"
                               onChange={(e) => {
                                   setOwnBookAuthor(e.target.value);
                               }}
                    />

                    {/* Checkbox to mark this book as read */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={ownBookMarkAsRead}
                                onChange={(e) => setOwnBookMarkAsRead(e.target.checked)}
                                name="markAsRead"
                                color="primary"
                            />
                        }
                        label="Mark as read"
                    />
                    <br/>

                </DialogContent>
                <DialogActions className="dialog-actions">
                    <Button className="dialog-button" color="primary" onClick={() => {
                        const manualBook = {
                            author_name: [ownBookAuthor],
                            title: ownBookTitle,
                            subject: [],
                            key: `manual-${Date.now()}`,
                        };
                        addToLibrary(manualBook);
                        setShowAddBookManuallyDialog(false);
                        setOwnBookTitle("");
                        setOwnBookAuthor("");
                        setOwnBookMarkAsRead(false);
                    }} disabled={ownBookTitle.length === 0 || ownBookAuthor.length === 0}>Add Book</Button>
                    <Button className="dialog-button" color="secondary" onClick={() => {
                        setShowAddBookManuallyDialog(false)
                        setOwnBookTitle("");
                        setOwnBookAuthor("");
                        setOwnBookMarkAsRead(false);
                    }}>Cancel</Button>
                </DialogActions>
            </Dialog>


            <Footer />
        </>
    );
};

export default withAuthenticationRequired(SearchComponent, {
    onRedirecting: () => <Loading/>,
});
